import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, Plus, Settings, LogOut } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { buttonVariants } from '@perch/ui';
import { PerchLogo } from '../ui/PerchLogo';

interface LandingHeaderProps {
  mode: 'landing';
  isScrolled: boolean;
  onGetStarted: () => void;
}

interface DashboardHeaderProps {
  mode: 'dashboard';
  user: User;
  onAddBooking?: () => void;
  onOpenSettings?: (tab?: string) => void;
  onSignOut: () => void;
}

interface LoadingHeaderProps {
  mode: 'loading';
}

type SiteHeaderProps = LandingHeaderProps | DashboardHeaderProps | LoadingHeaderProps;

/**
 * Unified header component used across the entire app.
 *
 * Three modes:
 * - landing: fixed position, transparent → frosted glass on scroll, nav links + CTA
 * - dashboard: sticky position, always frosted, add button + avatar menu
 * - loading: sticky position, just logo, no actions (auth loading state)
 */
export function SiteHeader(props: SiteHeaderProps) {
  const { mode } = props;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Position: fixed for landing (overlays hero), sticky for dashboard/loading
  const positionClasses = mode === 'landing'
    ? 'fixed top-0 left-0 right-0'
    : 'sticky top-0';

  // Background: landing transitions on scroll, dashboard/loading always frosted
  // Mobile menu needs an opaque background so content is readable
  const isScrolled = mode === 'landing' ? props.isScrolled : true;
  const needsOpaqueBg = mobileMenuOpen && mode === 'landing';
  const bgClasses = needsOpaqueBg
    ? 'bg-white dark:bg-stone-900 border-b border-stone-200/50 dark:border-stone-700/50'
    : isScrolled
      ? 'bg-white/80 dark:bg-stone-900/80 backdrop-blur-xl border-b border-stone-200/50 dark:border-stone-700/50'
      : '';

  // Safe area inset for dashboard/loading (mobile notch)
  const safeArea = mode !== 'landing' ? 'pt-[env(safe-area-inset-top)]' : '';

  return (
    <header
      className={`${positionClasses} z-40 transition-[background-color,border-color,backdrop-filter] duration-300 ${bgClasses} ${safeArea}`}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Brand — always links home */}
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <PerchLogo />
          </Link>

          {/* Right-side content */}
          {mode === 'landing' && (
            <div className="flex items-center gap-6">
              <a
                href="#how-it-works"
                className="hidden sm:block text-sm text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
              >
                How It Works
              </a>
              <a
                href="#pricing"
                className="hidden sm:block text-sm text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
              >
                Pricing
              </a>
              <button
                onClick={props.onGetStarted}
                className="hidden sm:block text-sm text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors cursor-pointer"
              >
                Sign In
              </button>
              <a
                href="#waitlist"
                className={buttonVariants({ variant: 'coral' })}
              >
                Join Waitlist
              </a>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-stone-500 dark:text-stone-400"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" strokeWidth={1.5} />
                ) : (
                  <Menu className="w-5 h-5" strokeWidth={1.5} />
                )}
              </button>
            </div>
          )}

          {mode === 'dashboard' && (
            <DashboardActions
              user={props.user}
              onAddBooking={props.onAddBooking}
              onOpenSettings={props.onOpenSettings}
              onSignOut={props.onSignOut}
            />
          )}
          {/* mode === 'loading' shows no right-side actions */}
        </div>

        {/* Mobile dropdown — landing only */}
        {mode === 'landing' && (
          <div
            className={`sm:hidden grid transition-[grid-template-rows,opacity] duration-300 ease-out ${mobileMenuOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
          >
            <div className="overflow-hidden">
              <div className="mt-3 pb-2 border-t border-stone-200/50 dark:border-stone-700/50 pt-3 space-y-1">
                <a
                  href="#how-it-works"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
                >
                  How It Works
                </a>
                <a
                  href="#pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
                >
                  Pricing
                </a>
                <a
                  href="#waitlist"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
                >
                  Join Waitlist
                </a>
                <button
                  onClick={() => { setMobileMenuOpen(false); props.onGetStarted(); }}
                  className="block py-2 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors cursor-pointer"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

// --- Dashboard mode actions (add button + avatar menu + dropdown) ---

function DashboardActions({
  user,
  onAddBooking,
  onOpenSettings,
  onSignOut,
}: {
  user: User;
  onAddBooking?: () => void;
  onOpenSettings?: (tab?: string) => void;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close menu on escape
  useEffect(() => {
    if (!menuOpen) return;
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  // Focus trap — cycle Tab within menu when open
  useEffect(() => {
    if (!menuOpen || !menuRef.current) return;
    const menu = menuRef.current;
    const focusableSelector = 'button, a, [tabindex]:not([tabindex="-1"])';

    function handleTab(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(menu.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [menuOpen]);

  const handleMenuItemClick = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  return (
    <div className="flex items-center gap-1">
      {/* Add flight — hidden on settings, shows label on desktop */}
      {onAddBooking && (
        <button
          onClick={onAddBooking}
          className="min-h-[44px] flex items-center gap-1.5 px-2 sm:px-3 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-colors"
          title="Add booking"
          aria-label="Add new flight to track"
        >
          <Plus className="w-5 h-5" strokeWidth={1.5} />
          <span className="hidden sm:inline text-sm font-medium">Add Flight</span>
        </button>
      )}

      {/* User menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="group min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          aria-expanded={menuOpen}
          aria-haspopup="true"
        >
          <div className="w-8 h-8 bg-stone-900 dark:bg-stone-100 rounded-full flex items-center justify-center text-sm font-medium text-stone-100 dark:text-stone-900 group-hover:opacity-80 transition-opacity">
            {user.email?.charAt(0).toUpperCase()}
          </div>
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-48 py-1 bg-white dark:bg-stone-900 rounded-xl shadow-lg border border-stone-200/60 dark:border-stone-800 overflow-hidden">
            {/* User email */}
            <div className="px-4 py-2.5 border-b border-stone-100 dark:border-stone-800">
              <p className="text-xs text-stone-400 dark:text-stone-500 truncate">
                {user.email}
              </p>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <button
                onClick={() => handleMenuItemClick(() => onOpenSettings ? onOpenSettings() : navigate('/settings'))}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors flex items-center gap-3"
              >
                <Settings className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                Settings
              </button>
            </div>

            {/* Sign out */}
            <div className="border-t border-stone-100 dark:border-stone-800 py-1">
              <button
                onClick={() => handleMenuItemClick(onSignOut)}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors flex items-center gap-3"
              >
                <LogOut className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
